import { createSlice } from '@reduxjs/toolkit';

export const helpArticlesSlice = createSlice({
    name: 'helpArticles',
    initialState: {
        // Admin article list
        isFetched: false,
        articles: [],
        // Single article (admin edit)
        currentArticle: null,
        isLoadingArticle: false,
        // Categories
        categories: [],
        isCategoriesFetched: false,
        // End-user article list (by category or all)
        tenantArticles: [],
        isTenantArticlesFetched: false,
        // End-user pinned articles (derived from tenantArticles)
        // End-user search results
        searchResults: [],
        isSearching: false,
        searchQuery: '',
        // End-user article view
        viewingArticle: null,
        isLoadingView: false,
        // End-user drawer nav state (persists across close/reopen within TTL)
        drawerView: 'list',
        drawerSelectedCategoryId: null,
        drawerLastClosedAt: null,
        // General
        isSubmitting: false
    },
    reducers: {
        // Admin article list
        onCallHelpArticles: (state) => {
            state.isFetched = false;
        },
        onSuccessHelpArticles: (state, { payload }) => {
            state.isFetched = true;
            state.articles = payload;
        },
        onFailedHelpArticles: (state) => {
            state.isFetched = true;
            state.articles = [];
        },

        // Single article
        onCallGetHelpArticle: (state) => {
            state.isLoadingArticle = true;
            state.currentArticle = null;
        },
        onSuccessGetHelpArticle: (state, { payload }) => {
            state.isLoadingArticle = false;
            state.currentArticle = payload;
        },
        onFailedGetHelpArticle: (state) => {
            state.isLoadingArticle = false;
            state.currentArticle = null;
        },

        // Create / Update / Delete
        setSubmitting: (state, { payload }) => {
            state.isSubmitting = payload !== undefined ? payload : !state.isSubmitting;
        },
        onSuccessCreateHelpArticle: (state, { payload }) => {
            state.isSubmitting = false;
            state.articles = [payload, ...state.articles];
        },
        onSuccessUpdateHelpArticle: (state, { payload }) => {
            state.isSubmitting = false;
            state.articles = state.articles.map(a => a.help_article_id === payload.help_article_id ? payload : a);
            if (state.currentArticle?.help_article_id === payload.help_article_id) {
                state.currentArticle = payload;
            }
        },
        onSuccessDeleteHelpArticle: (state, { payload }) => {
            state.isSubmitting = false;
            state.articles = state.articles.filter(a => a.help_article_id !== payload);
        },

        // Categories
        onCallHelpCategories: (state) => {
            state.isCategoriesFetched = false;
        },
        onSuccessHelpCategories: (state, { payload }) => {
            state.isCategoriesFetched = true;
            state.categories = payload;
        },
        onFailedHelpCategories: (state) => {
            state.isCategoriesFetched = true;
            state.categories = [];
        },
        onSuccessCreateHelpCategory: (state, { payload }) => {
            state.isSubmitting = false;
            state.categories = [...state.categories, payload];
        },
        onSuccessUpdateHelpCategory: (state, { payload }) => {
            state.isSubmitting = false;
            state.categories = state.categories.map(c => c.help_category_id === payload.help_category_id ? payload : c);
        },
        onSuccessDeleteHelpCategory: (state, { payload }) => {
            state.isSubmitting = false;
            state.categories = state.categories.filter(c => c.help_category_id !== payload);
        },

        // End-user tenant articles
        onCallTenantArticles: (state) => {
            state.isTenantArticlesFetched = false;
        },
        onSuccessTenantArticles: (state, { payload }) => {
            state.isTenantArticlesFetched = true;
            state.tenantArticles = payload;
        },
        onFailedTenantArticles: (state) => {
            state.isTenantArticlesFetched = true;
            state.tenantArticles = [];
        },

        // End-user search
        onCallSearchHelpArticles: (state, { payload }) => {
            state.isSearching = true;
            state.searchQuery = payload || '';
        },
        onSuccessSearchHelpArticles: (state, { payload }) => {
            state.isSearching = false;
            state.searchResults = payload;
        },
        onFailedSearchHelpArticles: (state) => {
            state.isSearching = false;
            state.searchResults = [];
        },

        // End-user article view by slug
        onCallGetHelpArticleBySlug: (state) => {
            state.isLoadingView = true;
        },
        onSuccessGetHelpArticleBySlug: (state, { payload }) => {
            state.isLoadingView = false;
            state.viewingArticle = payload;
        },
        onFailedGetHelpArticleBySlug: (state) => {
            state.isLoadingView = false;
            state.viewingArticle = null;
        },

        clearViewingArticle: (state) => {
            state.viewingArticle = null;
        },
        clearSearchResults: (state) => {
            state.searchResults = [];
            state.searchQuery = '';
            state.isSearching = false;
        },

        // Drawer nav state
        setHelpDrawerView: (state, { payload }) => {
            state.drawerView = payload;
        },
        setHelpDrawerSelectedCategoryId: (state, { payload }) => {
            state.drawerSelectedCategoryId = payload;
        },
        markHelpDrawerClosed: (state) => {
            state.drawerLastClosedAt = Date.now();
        },
        resetHelpDrawer: (state) => {
            state.drawerView = 'list';
            state.drawerSelectedCategoryId = null;
            state.drawerLastClosedAt = null;
        }
    }
});

export const {
    onCallHelpArticles,
    onSuccessHelpArticles,
    onFailedHelpArticles,
    onCallGetHelpArticle,
    onSuccessGetHelpArticle,
    onFailedGetHelpArticle,
    setSubmitting,
    onSuccessCreateHelpArticle,
    onSuccessUpdateHelpArticle,
    onSuccessDeleteHelpArticle,
    onCallHelpCategories,
    onSuccessHelpCategories,
    onFailedHelpCategories,
    onSuccessCreateHelpCategory,
    onSuccessUpdateHelpCategory,
    onSuccessDeleteHelpCategory,
    onCallTenantArticles,
    onSuccessTenantArticles,
    onFailedTenantArticles,
    onCallSearchHelpArticles,
    onSuccessSearchHelpArticles,
    onFailedSearchHelpArticles,
    onCallGetHelpArticleBySlug,
    onSuccessGetHelpArticleBySlug,
    onFailedGetHelpArticleBySlug,
    clearViewingArticle,
    clearSearchResults,
    setHelpDrawerView,
    setHelpDrawerSelectedCategoryId,
    markHelpDrawerClosed,
    resetHelpDrawer
} = helpArticlesSlice.actions;

export default helpArticlesSlice.reducer;
